import React from 'react';
import PropTypes from 'prop-types';
import SovosEditableSlidingPanel from 's1-ui/sovos-sliding-panel/SovosEditableSlidingPanel';
import SovosRaisedButton from 's1-ui/sovos-raised-button/SovosRaisedButton';
import SovosButtonsGroup from 's1-ui/sovos-buttons-group/SovosButtonsGroup';
import RuleEditor from '../ruleEditor/RuleEditorConnector';
import ExpressionParser from 'shared/helpers/ExpresionParser';
import {
  SLIDING_PANEL_DIALOG_ACTION_LABEL,
  SLIDING_PANEL_DIALOG_CONTENT_CLASS_NAME,
  SLIDING_PANEL_DIALOG_TEXT,
  SLIDING_PANEL_DIALOG_TITLE,
  SLIDING_PANEL_DIALOG_TITLE_CLASS_NAME
} from 'shared/constants/SharedConstants';

const initialRuleState = {
  id: null,
  name: '',
  description: '',
  ifValue: '',
  thenValue: '',
  thenCondition: null,
  allocationUnit: {
    id: 0,
    name: ''
  },
  costObjects: [
    {
      id: 0,
      name: '',
      amount: '',
      percentageOfGross: ''
    }
  ]
};
const allocationRuleSubtype = 4;
const customRuleSubtype = 0;

class RuleEditorContainer extends React.PureComponent {
  state = {
    openRuleEditorSlidingPanel: false,
    saveRuleDisabled: true,
    ruleInformation: { ...initialRuleState }
  };

  componentWillReceiveProps(nextProps) {
    if (this.props.creatingRule && !nextProps.creatingRule) {
      this.setState({ openRuleEditorSlidingPanel: false });
    }
    if (this.props.fetchingRule && !nextProps.fetchingRule) {
      const allocationRule = nextProps.rule.allocationUnit
        ? nextProps.rule
        : { ...nextProps.rule, allocationUnit: { id: 0, name: '' } };
      const rule = nextProps.rule.allocationUnit
        ? nextProps.rule
        : {
          ...nextProps.rule,
          allocationUnit: { id: 0, name: '' },
          costObjects: [
            { id: 0, name: '', amount: '', percentageOfGross: '' }
          ]
        };
      this.setState({
        openRuleEditorSlidingPanel: true,
        ruleInformation:
          nextProps.rule.thenCondition !== allocationRuleSubtype
            ? rule
            : allocationRule
      });
    }
  }

  handleOpenRuleEditorSlidingPanel = () =>
    this.setState({ openRuleEditorSlidingPanel: true });

  handleCreateRuleClick = () => {
    this.setState({ ruleInformation: { ...initialRuleState } });
    this.handleOpenRuleEditorSlidingPanel();
  };

  handleCloseRuleEditorSlidingPanel = () => {
    this.props.rulesActions.resetRuleCreator();
    this.setState({ openRuleEditorSlidingPanel: false });
  };

  calculateThenValue = (thenCondition, thenValue) =>
    thenCondition === customRuleSubtype
      ? thenValue
      : `[IL_LINESTATUSID] = ${thenCondition}`;

  handleSave = () => {
    const { ruleInformation } = this.state;

    if (ruleInformation.thenCondition !== allocationRuleSubtype) {
      if (ruleInformation.id) {
        this.handleEditRule(ruleInformation);
      } else {
        this.handleCreateRule(ruleInformation);
      }
    } else {
      if (ruleInformation.id) {
        this.handleUpdateAllocationRule(ruleInformation);
      } else {
        this.handleCreateAllocationRule(ruleInformation);
      }
    }
  };

  handleCreateRule = rule => {
    const { name, description, ifValue, thenValue, thenCondition } = rule;
    this.props.rulesActions.createRule(
      {
        name,
        description,
        subtype: thenCondition,
        customRuleAudit: {
          ifUserInput: ifValue,
          ifJsonDefinition: ExpressionParser.parseRule(ifValue).jsonObject,
          thenUserInput: this.calculateThenValue(thenCondition, thenValue),
          thenJsonDefinition: this.calculateThenValue(thenCondition, thenValue)
        }
      },
      this.props.pageState
    );
  };

  handleEditRule = rule => {
    const { name, description, ifValue, thenValue, thenCondition, id } = rule;
    this.props.rulesActions.updateRule(
      {
        id,
        name,
        description,
        subtype: thenCondition,
        customRuleAudit: {
          ifUserInput: ifValue,
          ifJsonDefinition: ExpressionParser.parseRule(ifValue).jsonObject,
          thenUserInput: this.calculateThenValue(thenCondition, thenValue),
          thenJsonDefinition: this.calculateThenValue(thenCondition, thenValue)
        }
      },
      this.props.pageState
    );
  };

  handleCreateAllocationRule = rule => {
    const {
      name,
      description,
      ifValue,
      thenCondition,
      costObjects,
      allocationUnit
    } = rule;
    this.props.rulesActions.createAllocationRule(
      {
        name,
        description,
        subtype: thenCondition,
        postCalculation: false,
        customRuleAudit: {
          ifUserInput: ifValue,
          ifJsonDefinition: ExpressionParser.parseRule(ifValue).jsonObject
        },
        costObjects: costObjects,
        allocationUnit: allocationUnit.id !== 0 ? allocationUnit : null
      },
      this.props.pageState
    );
  };

  handleUpdateAllocationRule = rule => {
    const {
      id,
      name,
      description,
      ifValue,
      thenCondition,
      costObjects,
      allocationUnit
    } = rule;
    this.props.rulesActions.updateAllocationRule(
      {
        id,
        name,
        description,
        subtype: thenCondition,
        postCalculation: false,
        customRuleAudit: {
          ifUserInput: ifValue,
          ifJsonDefinition: ExpressionParser.parseRule(ifValue).jsonObject
        },
        costObjects: costObjects,
        allocationUnit: allocationUnit.id !== 0 ? allocationUnit : null
      },
      this.props.pageState
    );
  };

  isSaveButtonDisabled = state =>
    (state.thenCondition === customRuleSubtype && state.thenValue === '') ||
    state.thenCondition === null ||
    state.name === '' ||
    (state.thenCondition === allocationRuleSubtype &&
      ((state.allocationUnit.id === 0 &&
        state.costObjects.filter(
          costObject =>
            costObject.percentageOfGross > 100 ||
            costObject.percentageOfGross <= 0 ||
            costObject.percentageOfGross === ''
        ).length !== 0) ||
        this.calculateSumPercentageOfGross(state.costObjects) ||
        state.costObjects.filter(costObject => costObject.id === 0).length !==
          0));

  handleRuleFieldChange = (fieldName, value) =>
    this.setState(oldState => {
      const ruleInformation = {
        ...oldState.ruleInformation,
        [fieldName]: value
      };
      return {
        ruleInformation,
        saveRuleDisabled: this.isSaveButtonDisabled(ruleInformation)
      };
    });

  handleAllocationUnitChange = newAllocationUnit => {
    this.setState(oldState => ({
      ruleInformation: {
        ...oldState.ruleInformation,
        allocationUnit: newAllocationUnit
          ? newAllocationUnit
          : { id: 0, name: '' }
      }
    }));
  };

  handleCostObjectChange = newCostObjectList =>
    this.setState(oldState => {
      const ruleInformation = {
        ...oldState.ruleInformation,
        costObjects: newCostObjectList
      };
      return {
        ruleInformation,
        saveRuleDisabled: this.isSaveButtonDisabled(ruleInformation)
      };
    });

  calculateSumPercentageOfGross = costObjects => {
    let sumPercentageOfGross = 0;
    costObjects.forEach(costObject => {
      sumPercentageOfGross += parseInt(
        costObject.percentageOfGross === '' ? 0 : costObject.percentageOfGross
      );
    });
    return sumPercentageOfGross > 100;
  };

  render() {
    return (
      <div>
        <SovosButtonsGroup
          align="right"
          style={ { marginTop: 25, marginBottom: 15 } }
        >
          <SovosRaisedButton onClick={ this.handleCreateRuleClick }>
            CREATE RULE
          </SovosRaisedButton>
        </SovosButtonsGroup>
        <SovosEditableSlidingPanel
          isDirty={ () => true }
          contentStyle={ { padding: 0 } }
          open={ this.state.openRuleEditorSlidingPanel }
          onClose={ this.handleCloseRuleEditorSlidingPanel }
          showCloseButton={ false }
          baseWidth={ '1080px' }
          title="Rule Builder"
          SaveButtonProps={ {
            onClick: this.handleSave,
            disabled: this.state.saveRuleDisabled || this.props.creatingRule,
            className: 'rule-builder__button'
          } }
          CancelButtonProps={ {
            className: 'rule-builder__button',
            children: 'CANCEL'
          } }
          CloseDialogProps={ {
            DialogTitleProps: {
              children: SLIDING_PANEL_DIALOG_TITLE,
              className: SLIDING_PANEL_DIALOG_TITLE_CLASS_NAME
            },
            DialogContentProps: {
              children: SLIDING_PANEL_DIALOG_TEXT,
              className: SLIDING_PANEL_DIALOG_CONTENT_CLASS_NAME
            },
            OKButtonProps: {
              children: SLIDING_PANEL_DIALOG_ACTION_LABEL
            },
            CancelButtonProps: {
              children: 'CANCEL'
            }
          } }
        >
          <RuleEditor
            onCancel={ this.handleCloseRuleEditorSlidingPanel }
            onSave={ this.handleSave }
            rule={ this.state.ruleInformation }
            onChangeRuleField={ this.handleRuleFieldChange }
            saveDisabled={ this.state.saveRuleDisabled }
            onAllocationUnitChange={ newAllocationUnit =>
              this.handleAllocationUnitChange(newAllocationUnit)
            }
            onCostObjectChange={ newCostObject =>
              this.handleCostObjectChange(newCostObject)
            }
          />
        </SovosEditableSlidingPanel>
      </div>
    );
  }
}

RuleEditorContainer.propTypes = {
  fetchingRule: PropTypes.bool.isRequired,
  rule: PropTypes.object.isRequired,
  creatingRule: PropTypes.bool.isRequired,
  pageState: PropTypes.object,
  rulesActions: PropTypes.shape({
    resetRuleCreator: PropTypes.func,
    createRule: PropTypes.func,
    updateRule: PropTypes.func,
    createAllocationRule: PropTypes.func,
    updateAllocationRule: PropTypes.func
  }).isRequired
};

RuleEditorContainer.defaultProps = {
  pageState: {}
};

export default RuleEditorContainer;



// WEBPACK FOOTER //
// ./src/components/manageRules/comp/ruleEditorContainer/RuleEditorContainer.js